import type { StavZakazky, ZakazkaListItem, ZoznamCutriteItem } from "@/api/schemas";
import { STAV_LABELS } from "@/components/zakazka/StavBadge";
import { sklonuj } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Bunky prehľadu po pohľadoch (KOLO 242, návrh P4) — cesta zákazky,
 * časti výroby, Cut Rite a objednávky. Dáta nesie riadok zoznamu (jeden
 * dotaz na stranu), nič sa tu nedopytuje.
 */

const CESTA: StavZakazky[] = ["ponuka", "v_priprave", "vo_vyrobe", "dokoncena"];

/** Krok cesty: 1 Ponuka … 4 Dokončená, 5 = aj kalkulácia. */
export function krokCesty(z: Pick<ZakazkaListItem, "stav" | "fakturovana">): number {
  if (z.fakturovana) return 5;
  return CESTA.indexOf(z.stav) + 1;
}

/**
 * Cesta Ponuka → V príprave → Vo výrobe → Dokončená → Kalkulácia ako
 * bodky (●●●○○) so stavom — „kde je" zákazka bez otvárania.
 */
export function CestaZakazky({
  zakazka,
  className,
}: {
  zakazka: Pick<ZakazkaListItem, "stav" | "fakturovana">;
  className?: string;
}): JSX.Element {
  const krok = krokCesty(zakazka);
  const kroky = [...CESTA.map((s) => STAV_LABELS[s]), "Kalkulácia"];
  const text = zakazka.fakturovana ? "Kalkulácia" : STAV_LABELS[zakazka.stav];
  return (
    <span
      className={cn("inline-flex items-center gap-2 whitespace-nowrap", className)}
      title={kroky.map((k, i) => `${i < krok ? "✓" : "·"} ${k}`).join("\n")}
      data-testid="cesta-zakazky"
    >
      <span className="flex items-center gap-0.5" aria-hidden>
        {kroky.map((k, i) => (
          <span
            key={k}
            className={cn(
              "h-2 w-2 rounded-full",
              i + 1 < krok
                ? "bg-tandem-gray-dark"
                : i + 1 === krok
                  ? "bg-primary"
                  : "border border-tandem-gray-light bg-background",
            )}
          />
        ))}
      </span>
      <span className="text-xs font-medium">{text}</span>
    </span>
  );
}

/** Časti výroby — mená častí, „celá zákazka", malá zákazka, bez výroby. */
export function popisCasti(
  z: Pick<ZakazkaListItem, "casti_nazvy" | "ma_vyrobu" | "cislo_z">,
): string {
  const casti = z.casti_nazvy ?? [];
  if (casti.length >= 2) return casti.join(", ");
  if (z.cislo_z) return "malá zákazka";
  if (z.ma_vyrobu) return "celá zákazka";
  return "bez výroby";
}

type TonZoznamu = "nic" | "bezi" | "ok" | "chyba";

/** Stav jedného zoznamu v Cut Rite po slovách a farbe (ako okno Cut Rite). */
export function stavZoznamu(c: ZoznamCutriteItem): { text: string; ton: TonZoznamu } {
  if (c.stav === "chyba") return { text: "chyba v Cut Rite", ton: "chyba" };
  if (c.stav !== "hotovo") return { text: "v Cut Rite", ton: "bezi" };
  switch (c.pila_stav) {
    case "hotovo":
      return { text: "na píle", ton: "ok" };
    case "caka":
    case "prevzate":
      return { text: "ide na pílu", ton: "bezi" };
    case "chyba":
      return { text: "píla — chyba", ton: "chyba" };
    default:
      return { text: "optimalizované", ton: "ok" };
  }
}

const FARBA_TONU: Record<TonZoznamu, string> = {
  nic: "bg-tandem-gray-light",
  bezi: "bg-amber-500",
  ok: "bg-emerald-600",
  chyba: "bg-destructive",
};

/** Zoznamy zákazky v Cut Rite — riadok na zoznam („Pult · na píle"). */
export function CutriteBunka({ zakazka }: { zakazka: Pick<ZakazkaListItem, "cutrite"> }): JSX.Element {
  const zoznamy = zakazka.cutrite ?? [];
  if (zoznamy.length === 0) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className={cn("h-1.5 w-1.5 rounded-full", FARBA_TONU.nic)} aria-hidden />
        neposlané
      </span>
    );
  }
  return (
    <span className="flex flex-col gap-0.5" data-testid="cutrite-bunka">
      {zoznamy.map((c) => {
        const { text, ton } = stavZoznamu(c);
        return (
          <span key={c.nazov} className="flex items-center gap-1.5 whitespace-nowrap text-xs">
            <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", FARBA_TONU[ton])} aria-hidden />
            {c.nazov ? <span className="truncate font-medium">{c.nazov} ·</span> : null}
            <span className={cn(ton === "chyba" && "text-destructive")}>{text}</span>
          </span>
        );
      })}
    </span>
  );
}

/**
 * Objednávky — koľko položiek je objednaných, prijatých, zo skladu
 * a rezervovaných. Bez celku „z M": ten je súpis materiálu (KOLO 242).
 */
export function popisObjednavok(o: Record<string, number> | undefined): string | null {
  if (!o) return null;
  const objednane = (o.objednane ?? 0) + (o.prijate ?? 0) + (o.zo_skladu ?? 0);
  const casti: string[] = [];
  if (objednane) casti.push(`${objednane} ${sklonuj(objednane, ["objednaná", "objednané", "objednaných"])}`);
  if (o.prijate) casti.push(`${o.prijate} ${sklonuj(o.prijate, ["prijatá", "prijaté", "prijatých"])}`);
  if (o.rezervovane) {
    casti.push(
      `${o.rezervovane} ${sklonuj(o.rezervovane, ["rezervovaná", "rezervované", "rezervovaných"])}`,
    );
  }
  return casti.length ? casti.join(" · ") : null;
}

export function ObjednavkyBunka({
  zakazka,
}: {
  zakazka: Pick<ZakazkaListItem, "objednavky">;
}): JSX.Element {
  const text = popisObjednavok(zakazka.objednavky);
  return text ? (
    <span
      className="flex flex-col whitespace-nowrap text-xs"
      title="Položky z Objednávok zákazky (objednané zahŕňajú prijaté)"
    >
      {text.split(" · ").map((cast) => (
        <span key={cast}>{cast}</span>
      ))}
    </span>
  ) : (
    <span className="text-xs text-muted-foreground" title="Na Objednávkach zatiaľ nič">
      —
    </span>
  );
}

/** „Kde je" prijatá zákazka obchodníka — Cut Rite, kalkulácia, objednávky. */
export function kdeJe(z: ZakazkaListItem): string {
  if (z.fakturovana) return "kalkulácia hotová";
  const zoznamy = (z.cutrite ?? []).map((c) => {
    const { text } = stavZoznamu(c);
    return c.nazov ? `${c.nazov} ${text}` : text;
  });
  if (zoznamy.length) return zoznamy.join(", ");
  if (z.stav === "dokoncena") return "vyrobená, čaká na kalkuláciu";
  const obj = popisObjednavok(z.objednavky);
  if (obj) return obj;
  return z.ma_vyrobu ? "výroba pripravená, ešte nie v Cut Rite" : "čaká na import výroby";
}
